import React, { useState, useEffect, useRef } from "react";
import { FiUpload, FiUsers, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

export default function AddUserForm({ theme }) {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    roleId: "", // Role ID
    agentId: "", // Only used if roleId === 4
  });
  const [roles, setRoles] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const isMounted = useRef(true);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/roles`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (isMounted.current) setRoles(data);
      } catch (error) {
        if (isMounted.current) {
          console.error("Failed to fetch roles", error);
          setToast({ type: "error", message: "Failed to load roles." });
        }
      }
    };

    fetchRoles();

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (parseInt(form.roleId) === 4) {
      setLoadingAgents(true);
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agents`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data)) setAgents(data);
        })
        .catch(() =>
          setToast({ type: "error", message: "Failed to load agents." })
        )
        .finally(() => setLoadingAgents(false));
    } else {
      setAgents([]);
      setForm((prev) => ({ ...prev, agentId: "" }));
    }
  }, [form.roleId]);

  const toggleOpen = () => setIsOpen((prev) => !prev);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const userStr = localStorage.getItem("user");
    const user = JSON.parse(userStr);
    const userId = user.id;

    try {
      const payload = {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        created_by: userId,
        role_id: Number(form.roleId),
      };

      if (parseInt(form.roleId) === 4) {
        payload.agent_id = Number(form.agentId);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/create-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok) throw new Error(data.error || "Failed to add user.");

      showToast("success", data.message);
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        roleId: "",
        agentId: "",
      });
      // setIsOpen(false);
    } catch (error) {
      showToast("error", error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto mt-12 p-8 bg-white rounded-3xl shadow-2xl border border-gray-100">
      <button
        aria-controls="add-user-form"
        onClick={toggleOpen}
        className="flex justify-between items-center w-full text-left text-3xl font-bold text-gray-900 border-b border-gray-300 pb-4 hover:text-blue-600 transition-colors"
      >
        <span>Add New User</span>
        {isOpen ? (
          <FiChevronUp size={28} className="text-blue-600" />
        ) : (
          <FiChevronDown size={28} />
        )}
      </button>

      <AnimatePresence>
        <motion.form
          id="add-user-form"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: 1,
            height: "auto",
            transition: { duration: 0.4 },
          }}
          exit={{ opacity: 0, height: 0, transition: { duration: 0.3 } }}
          className="mt-8 overflow-hidden space-y-8"
        >
          {/* Section Header */}
          <div className="flex items-center gap-3 text-xl font-semibold text-blue-700 border-b pb-3">
            <FiUsers size={22} />
            <span>User Details</span>
          </div>

          {/* First & Last Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                placeholder="e.g. John"
                required
                className="w-full border border-gray-300 rounded-xl px-5 py-3 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
            </div>

            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Last Name
              </label>
              <input
                id="lastName"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                placeholder="e.g. Doe"
                required
                className="w-full border border-gray-300 rounded-xl px-5 py-3 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="e.g. john.doe@example.com"
              required
              className="w-full border border-gray-300 rounded-xl px-5 py-3 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            />
          </div>

          {/* Role */}
          <div className="relative">
            <label
              htmlFor="roleId"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Select Role
            </label>
            <div className="relative">
              <select
                id="roleId"
                name="roleId"
                value={form.roleId}
                onChange={handleChange}
                required
                className="w-full appearance-none border border-gray-300 rounded-xl px-5 py-3 text-base bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="" disabled>
                  Select a role
                </option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.role}
                  </option>
                ))}
              </select>
              <FiChevronDown className="absolute right-4 top-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Agent Dropdown (Conditional) */}
          {parseInt(form.roleId) === 4 && (
            <div className="relative">
              <label
                htmlFor="agentId"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Assign Agent
              </label>
              <div className="relative">
                <select
                  id="agentId"
                  name="agentId"
                  value={form.agentId}
                  onChange={handleChange}
                  required
                  className="w-full appearance-none border border-gray-300 rounded-xl px-5 py-3 text-base bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                >
                  <option value="" disabled>
                    {loadingAgents ? "Loading agents..." : "Select agent"}
                  </option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name || `${a.firstName} ${a.lastName}`}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-4 top-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <motion.button
              type="submit"
              disabled={submitting}
              whileTap={{ scale: 0.97 }}
              className={`flex items-center gap-2 rounded-full px-8 py-3 text-base font-semibold text-white shadow-md transition ${
                submitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-400"
              }`}
            >
              {submitting ? "Adding..." : "Add User"}
              <FiUpload size={20} />
            </motion.button>
          </div>
        </motion.form>
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            className={`fixed bottom-8 right-8 z-50 px-5 py-3 rounded-xl shadow-lg text-white font-medium ${
              toast.type === "success" ? "bg-green-600" : "bg-red-600"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
